import { supabase } from './supabase';

export interface Brand {
  id: string;
  name: string;
  slug?: string;
  logo_url?: string;
  website_url?: string;
  description?: string;
  country_of_origin?: string;
  founded_year?: number;
  verified?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface BrandSupplierRelationship {
  id: string;
  brand_id: string;
  supplier_id: string;
  relationship_type: string;
  territory?: string;
  is_verified: boolean;
  verification_document_url?: string;
  start_date?: string;
  end_date?: string;
  created_at?: string;
  updated_at?: string;
  brands?: {
    id: string;
    name: string;
    logo_url?: string;
    verified?: boolean;
  };
}

export interface BrandSeller {
  id: string;
  brand_id: string;
  supplier_id: string;
  relationship_type: 'manufacturer' | 'distributor' | 'dealer';
  verified: boolean;
  valid_until?: string;
  created_at?: string;
  updated_at?: string;
  suppliers?: {
    id: string;
    name: string;
    logo?: string;
    location?: string;
  };
}

export class BrandService {
  // Get supplier-brand relationships
  async getSupplierBrandRelationships(supplierId: string): Promise<BrandSupplierRelationship[]> {
    try {
      const { data, error } = await supabase
        .from('brand_suppliers')
        .select(`
          *,
          brands (id, name, logo_url, verified)
        `)
        .eq('supplier_id', supplierId);
        
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching supplier brand relationships:', error);
      return [];
    }
  }
  
  // Create a brand-supplier relationship
  async createBrandSupplierRelationship(
    brandId: string, 
    supplierId: string, 
    relationshipType: string, 
    territory?: string
  ): Promise<BrandSupplierRelationship | null> {
    try {
      const { data, error } = await supabase
        .from('brand_suppliers')
        .insert({
          brand_id: brandId,
          supplier_id: supplierId,
          relationship_type: relationshipType,
          territory,
          is_verified: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select();
      
      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('Error creating brand-supplier relationship:', error);
      return null;
    }
  }
  
  // Verify a brand-supplier relationship (admin only)
  async verifyBrandSupplierRelationship(id: string, verificationDocumentUrl?: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('brand_suppliers')
        .update({
          is_verified: true,
          verification_document_url: verificationDocumentUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
        
      return !error;
    } catch (error) {
      console.error('Error verifying brand-supplier relationship:', error);
      return false;
    }
  }
  
  // Delete a brand-supplier relationship
  async deleteBrandSupplierRelationship(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('brand_suppliers')
        .delete()
        .eq('id', id);
        
      return !error;
    } catch (error) {
      console.error('Error deleting brand-supplier relationship:', error);
      return false;
    }
  }
  
  // Get brand-supplier relationship by brand and supplier
  async getBrandSupplierRelationship(brandId: string, supplierId: string): Promise<BrandSupplierRelationship | null> {
    try {
      const { data, error } = await supabase
        .from('brand_suppliers')
        .select(`
          *,
          brands (id, name, logo_url, verified)
        `)
        .eq('brand_id', brandId)
        .eq('supplier_id', supplierId)
        .maybeSingle();
        
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching brand-supplier relationship:', error);
      return null;
    }
  }
  
  async getBrands(): Promise<Brand[]> {
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .order('name');
        
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching brands:', error);
      return [];
    }
  }
  
  async getBrand(id: string): Promise<Brand | null> {
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching brand:', error);
      return null;
    }
  }
  
  async getBrandBySlug(slug: string): Promise<Brand | null> {
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .eq('slug', slug)
        .single();
        
      if (error) {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching brand by slug:', error);
      return null;
    }
  }
  
  async getProductsByBrand(brandId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          suppliers:supplier_id (name)
        `)
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching products by brand:', error);
      return [];
    }
  }
  
  async getAuthorizedSellers(brandId: string): Promise<BrandSeller[]> {
    try {
      const { data, error } = await supabase
        .from('brand_sellers')
        .select(`
          *,
          suppliers:supplier_id (id, name, logo, location)
        `)
        .eq('brand_id', brandId);
        
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching authorized sellers:', error);
      return [];
    }
  }
  
  async createBrand(brand: Omit<Brand, 'id'>): Promise<Brand | null> {
    try {
      const { data, error } = await supabase
        .from('brands')
        .insert([{
          ...brand,
          slug: this.generateSlug(brand.name),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select();
        
      if (error) {
        throw error;
      }
      
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('Error creating brand:', error);
      return null;
    }
  }
  
  async updateBrand(id: string, updates: Partial<Brand>): Promise<Brand | null> {
    try {
      const { data, error } = await supabase
        .from('brands')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select();
        
      if (error) {
        throw error;
      }
      
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('Error updating brand:', error);
      return null;
    }
  }
  
  async addAuthorizedSeller(brandSeller: Omit<BrandSeller, 'id'>): Promise<BrandSeller | null> {
    try {
      const { data, error } = await supabase
        .from('brand_sellers')
        .insert([{
          ...brandSeller,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select();
        
      if (error) {
        throw error;
      }
      
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('Error adding authorized seller:', error);
      return null;
    }
  }
  
  async removeAuthorizedSeller(brandId: string, supplierId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('brand_sellers')
        .delete()
        .eq('brand_id', brandId)
        .eq('supplier_id', supplierId);
        
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error removing authorized seller:', error);
      return false;
    }
  }
  
  async checkBrandExists(name: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('id')
        .eq('name', name)
        .maybeSingle();
        
      if (error) {
        throw error;
      }
      
      return !!data;
    } catch (error) {
      console.error('Error checking brand existence:', error);
      return false;
    }
  }
  
  private generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }
}
